import AddIcon from "@mui/icons-material/Add";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert, Box, Button, Chip, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { api } from "../api/client";
import type { CostDocument, Paginated } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { BackButton } from "../components/BackButton";
import { NIEPRZYPISANE, STAN_KOSZTU } from "../lib/costState";
import { formatMoney, sumMoney } from "../lib/money";
import { RegisterCostDialog } from "./RegisterCostDialog";

export function CostsPage() {
  const { code = "" } = useParams();
  const { can } = useAuth();
  const [otwarty, setOtwarty] = useState(false);

  const { data } = useQuery({
    queryKey: ["costs", code],
    queryFn: () => api.get<Paginated<CostDocument>>(`/projects/${code}/costs`),
  });

  const koszty = data?.results ?? [];
  const nieprzypisane = koszty.filter((k) => NIEPRZYPISANE.includes(k.state));

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <BackButton fallback={`/projekty/${code}`} />
        <Typography variant="h6">{code} · koszty</Typography>
        <Box sx={{ flex: 1 }} />
        {can("cost.register") && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOtwarty(true)}>
            Zarejestruj koszt
          </Button>
        )}
      </Stack>

      {/* Ta kolejka jest powodem, dla którego kolumna „rzeczywiste" bywa
          zaniżona — musi być widoczna, a nie wyliczalna z listy. */}
      {nieprzypisane.length > 0 && (
        <Alert severity="warning" icon={<WarningAmberIcon />}>
          {nieprzypisane.length} dok. na{" "}
          {formatMoney(sumMoney(nieprzypisane.map((k) => k.amount_net_control)))} zł
          nie ma jeszcze kompletnego przypisania. O tyle raport zaniża „Rzeczywiste”.
        </Alert>
      )}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Sprzedawca</TableCell>
              <TableCell>Numer</TableCell>
              <TableCell>Źródło</TableCell>
              <TableCell align="right">Netto</TableCell>
              <TableCell>Stan</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {koszty.map((k) => (
              <TableRow key={k.id} hover>
                <TableCell>{k.issued_on}</TableCell>
                <TableCell>
                  {k.seller_name_snapshot || k.seller_nip}
                  <Typography variant="caption" color="text.secondary" display="block">
                    NIP {k.seller_nip}
                  </Typography>
                </TableCell>
                <TableCell>{k.number}</TableCell>
                <TableCell>
                  {k.source === "ksef" ? (
                    <Tooltip title="Dokument żyje w KSeF i w GT — nie przechowujemy kopii.">
                      <span>KSeF</span>
                    </Tooltip>
                  ) : "skan"}
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {formatMoney(k.amount_net_control)}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={STAN_KOSZTU[k.state]?.label ?? k.state}
                        color={STAN_KOSZTU[k.state]?.color ?? "default"} />
                </TableCell>
                <TableCell align="right">
                  <Button component={Link} to={`/projekty/${code}/koszty/${k.id}`}>
                    {k.problems.length ? "Opisz" : "Otwórz"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {koszty.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    Brak zarejestrowanych kosztów.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <RegisterCostDialog open={otwarty} onClose={() => setOtwarty(false)} projectCode={code} />
    </Stack>
  );
}
